import React, { useEffect, useRef, useState } from 'react';
import { X, Trash2, BookmarkPlus, Loader2, FileText, Eye, Check, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export default function UploadPreviewModal({
  doc,
  isDark = true,
  canDelete = false,
  canApprove = false,
  canManageCrmDocument = false,
  onClose,
  onDeleted,
  onTemplated,
  onStatusChanged,
}) {
  const [url, setUrl] = useState(null);
  const [docxPdfUrl, setDocxPdfUrl] = useState(null);
  const [docxFallback, setDocxFallback] = useState(false);
  const [textHtml, setTextHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const docxContainerRef = useRef(null);
  const urlRef = useRef(null);
  const docxPdfUrlRef = useRef(null);

  const ext = (doc?.fileName || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  const isDocx = ext === 'docx';
  const isText = ['txt', 'csv', 'log', 'md', 'json', 'xml', 'html'].includes(ext);

  useEffect(() => {
    if (!doc) return undefined;
    let alive = true;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setDocxPdfUrl(null);
        setDocxFallback(false);
        setTextHtml(null);

        const r = await fetch(`${api.defaults.baseURL}/documents/${doc.id}/download`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!r.ok) throw new Error('preview failed');
        const blob = await r.blob();
        if (!alive) return;

        const u = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = u;
        setUrl(u);

        if (isDocx) {
          try {
            const pr = await fetch(`${api.defaults.baseURL}/documents/${doc.id}/preview-pdf`, {
              credentials: 'include',
              signal: controller.signal,
            });
            if (pr.ok) {
              const pdfBlob = await pr.blob();
              if (!alive) return;
              const pu = URL.createObjectURL(pdfBlob);
              if (docxPdfUrlRef.current) URL.revokeObjectURL(docxPdfUrlRef.current);
              docxPdfUrlRef.current = pu;
              setDocxPdfUrl(pu);
              return;
            }
          } catch {
            // Fall back to client-side rendering below.
          }

          setDocxFallback(true);
          requestAnimationFrame(async () => {
            if (!alive || !docxContainerRef.current) return;
            try {
              const docxPreview = await import('docx-preview');
              docxContainerRef.current.innerHTML = '';
              await docxPreview.renderAsync(blob, docxContainerRef.current, null, {
                className: 'docx-rendered',
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false,
                ignoreFonts: false,
                breakPages: true,
                experimental: true,
                useBase64URL: true,
              });
            } catch (err) {
              if (docxContainerRef.current) {
                docxContainerRef.current.innerHTML = `<p style="color:#ef4444;padding:20px">Could not render DOCX: ${err?.message || ''}</p>`;
              }
            }
          });
          return;
        }

        if (isText) {
          const textValue = await blob.text();
          if (alive) setTextHtml(textValue.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])));
        }
      } catch (err) {
        if (err.name !== 'AbortError') toast.error('Could not load preview');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      if (docxPdfUrlRef.current) {
        URL.revokeObjectURL(docxPdfUrlRef.current);
        docxPdfUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);

  if (!doc) return null;

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm('Delete this document?')) return;
    try {
      setBusy(true);
      await api.delete(`/documents/${doc.id}`);
      toast.success('Deleted');
      onDeleted?.(doc.id);
      onClose?.();
    } catch {
      toast.error('Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handleTemplate = async () => {
    if (!canManageCrmDocument) return;
    try {
      setBusy(true);
      await api.patch(`/documents/${doc.id}/template`, { isTemplate: true });
      toast.success('Saved as template');
      onTemplated?.(doc.id);
      onClose?.();
    } catch {
      toast.error('Could not save template');
    } finally {
      setBusy(false);
    }
  };

  const handleSetStatus = async (newStatus) => {
    if (!canApprove) return;
    try {
      setBusy(true);
      await api.patch(`/documents/${doc.id}/status`, { status: newStatus });
      toast.success(newStatus === 'approved' ? 'Document approved' : 'Document declined and deleted');
      onStatusChanged?.(doc.id, newStatus);
      onClose?.();
    } catch {
      toast.error('Could not update status');
    } finally {
      setBusy(false);
    }
  };

  const isClientUpload = doc?.uploaderRole === 'client';
  const bg = isDark ? 'bg-[#0f1729]' : 'bg-white';
  const border = isDark ? 'border-gray-800' : 'border-gray-200';
  const text = isDark ? 'text-white' : 'text-gray-900';
  const subtext = isDark ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className={`w-[90vw] max-w-5xl h-[90vh] rounded-xl flex flex-col ${bg} border ${border} overflow-hidden`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${border}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Eye size={18} className="text-purple-400 shrink-0" />
            <div className="min-w-0">
              <div className={`text-sm font-semibold ${text}`}>Document preview</div>
              <div className={`text-xs ${subtext} truncate max-w-[400px]`}>{doc.fileName}</div>
            </div>
          </div>
          <button onClick={onClose}
            className={`p-1.5 rounded hover:bg-red-500/20 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-black/30 flex items-stretch justify-center p-3">
          {loading ? (
            <div className={`flex items-center gap-2 ${subtext} self-center`}>
              <Loader2 className="animate-spin" size={18} /> Loading preview...
            </div>
          ) : isDocx && docxPdfUrl ? (
            <iframe src={docxPdfUrl} title="docx-as-pdf" className="w-full h-full bg-white rounded" />
          ) : isDocx && docxFallback ? (
            <div ref={docxContainerRef}
              className="w-full h-full bg-gray-200 rounded overflow-auto"
              style={{ color: '#111' }} />
          ) : url ? (
            isPdf ? (
              <iframe src={url} title="preview" className="w-full h-full bg-white rounded" />
            ) : isImage ? (
              <img src={url} alt={doc.fileName} className="max-w-full max-h-full object-contain self-center mx-auto" />
            ) : isText && textHtml !== null ? (
              <div className="w-full h-full bg-white rounded overflow-auto p-6">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, color: '#111', margin: 0 }}>
                  {textHtml.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')}
                </pre>
              </div>
            ) : (
              <div className={`flex flex-col items-center gap-3 ${subtext} self-center`}>
                <FileText size={48} />
                <div>Preview is not available for this file type</div>
                <a href={url} download={doc.fileName}
                  className="px-3 py-1.5 rounded bg-purple-600 text-white text-sm hover:bg-purple-700">
                  Download to review
                </a>
              </div>
            )
          ) : (
            <div className={`${subtext} self-center`}>Could not load preview</div>
          )}
        </div>

        <div className={`px-4 py-3 border-t ${border} flex items-center justify-between gap-3 flex-wrap`}>
          {canDelete ? (
            <button onClick={handleDelete} disabled={busy}
              className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
          ) : <span />}

          {isClientUpload ? (
            <div className="flex items-center gap-2">
              {canApprove && (
                <>
                  <button onClick={() => handleSetStatus('declined')} disabled={busy || doc.status === 'declined'}
                    className="px-4 py-2 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-medium flex items-center gap-2 disabled:opacity-50 border border-red-500/30">
                    <XCircle size={14} /> Decline
                  </button>
                  <button onClick={() => handleSetStatus('approved')} disabled={busy || doc.status === 'approved'}
                    className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                    <Check size={14} /> Approve
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {canApprove && doc.status !== 'approved' && (
                <button onClick={() => handleSetStatus('approved')} disabled={busy}
                  className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  <Check size={14} /> Approve
                </button>
              )}
              {canManageCrmDocument && (
                <button onClick={handleTemplate} disabled={busy}
                  className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-50 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-100 border border-gray-700' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'}`}>
                  <BookmarkPlus size={14} /> Save as template
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
